import React from 'react';
import { Link } from 'react-router-dom';
import { LegalLayout } from './LegalLayout';

export const Terms: React.FC = () => {
  return (
    <LegalLayout
      kicker="Legal"
      title="Terms of Service"
      lastUpdated="October 2, 2026"
      documentTitle="Terms of Service — PaperBrain"
      intro="Plain-English version: PaperBrain reads your handwritten pages, keeps them private to you, charges $14.99 a month after a free trial, and lets you cancel whenever. Below is the longer version."
    >
      <h2>1. Who we are</h2>
      <p>
        PaperBrain is a service, operated by Byte-Sized Bits, LLC ("PaperBrain",
        "we", "us", "our"), that digitizes and organizes handwritten notes you
        upload. By creating an
        account or otherwise using the service, you ("you", "your") agree to
        these Terms of Service.
      </p>

      <h2>2. Eligibility &amp; accounts</h2>
      <p>
        You must be at least 13 years old (or the minimum digital-consent age
        in your jurisdiction) to use PaperBrain. You're responsible for the
        activity that happens under your account, for keeping your password
        confidential, and for the accuracy of the email you provide.
      </p>
      <p>
        We may limit the rate of new account creation from a single IP address
        to prevent abuse.
      </p>

      <h2>3. The service</h2>
      <p>
        PaperBrain lets you upload images of handwritten or printed pages
        ("Content"), extracts text from them using third-party AI providers,
        and stores both the original image and the extracted text in your
        private library. Features include search, tagging, collections, and a
        notebook-aware AI workspace that answers questions citing your own
        pages.
      </p>

      <h2>4. Subscription &amp; billing</h2>
      <p>
        Our order process is conducted by our online reseller{' '}
        <strong>Paddle.com</strong>. Paddle.com is the Merchant of Record for
        all orders placed on paperbrain.dev; it handles payment, invoicing,
        sales tax, and order-related inquiries and returns, under its own{' '}
        <a href="https://www.paddle.com/legal/checkout-buyer-terms" target="_blank" rel="noreferrer">
          buyer terms
        </a>
        . Subscriptions bought inside our iPhone or Android app are sold and
        billed by Apple or Google under their terms instead.
      </p>
      <ul>
        <li>
          PaperBrain costs <strong>$14.99 per month</strong>, billed in
          advance. Applicable sales tax or VAT is included in that price.
        </li>
        <li>
          New accounts get a <strong>3-day free trial</strong>. We collect a
          payment method up front; you can cancel anytime during the trial
          and won't be charged. The trial is one per customer.
        </li>
        <li>
          Your subscription renews automatically each month until you cancel.
        </li>
        <li>
          You can cancel anytime from the billing portal. After cancellation
          you keep access until the end of the current billing period. You
          can request a full refund within 14 days of any payment — see our{' '}
          <Link to="/refunds">Refund Policy</Link>.
        </li>
        <li>
          Page allowances and feature lists may be tuned over time; we'll give
          reasonable notice of any change that materially reduces what you've
          signed up for.
        </li>
        <li>
          If a renewal payment fails, your access is paused straight away
          until the payment goes through. Our payment provider retries the
          charge, and access comes back as soon as you update your payment
          method or a retry succeeds. If the payment can't be recovered, the
          subscription is canceled. Your pages stay in your library either
          way.
        </li>
      </ul>

      <h2>5. Your content</h2>
      <p>
        You retain ownership of every page you upload and every byte of text
        we extract from it. You grant PaperBrain a limited license to store,
        process, transmit, and display your Content solely so we can run the
        service for you (for example: storing your uploads, sending pages to
        our OCR provider, displaying results back to you, and exporting your
        library when you ask).
      </p>
      <p>
        We do not train AI models on your Content, and we do not sell or share
        your Content with third parties for marketing.
      </p>

      <h2>6. Acceptable use</h2>
      <p>You agree not to upload Content or use the service to:</p>
      <ul>
        <li>break the law or anyone's intellectual-property or privacy rights;</li>
        <li>store or distribute illegal, harassing, or abusive material;</li>
        <li>circumvent rate limits, security controls, or our payment system;</li>
        <li>
          use the service to build a competing product, scrape data, or run
          automated extraction at scale without our written permission.
        </li>
      </ul>

      <h2>7. Third-party services</h2>
      <p>
        PaperBrain depends on a small number of third-party providers — for
        payments (Paddle, and Apple or Google for in-app purchases), hosting,
        and AI/OCR. We try to choose reputable
        partners, but we don't control their systems and aren't responsible
        for outages or errors caused by them. See our{' '}
        <Link to="/privacy">Privacy Policy</Link> for details about what they
        receive.
      </p>

      <h2>8. Termination</h2>
      <p>
        You can delete your account at any time by contacting us. We may
        suspend or terminate accounts that materially or repeatedly violate
        these Terms; we'll generally try to notify you and give you a chance
        to fix it first.
      </p>
      <p>
        On termination, your access ends and we will delete your Content
        within a reasonable period, except where retention is required by law
        or needed to handle disputes or enforce these Terms.
      </p>

      <h2>9. Disclaimers</h2>
      <p>
        PaperBrain is provided <strong>"as is"</strong>. OCR and AI output can
        be inaccurate; you're responsible for verifying anything important.
        We don't promise the service will be uninterrupted, error-free, or
        fit for any particular purpose, and to the fullest extent permitted
        by law we disclaim all implied warranties.
      </p>

      <h2>10. Limitation of liability</h2>
      <p>
        To the fullest extent permitted by law, PaperBrain's total liability
        for any claim arising out of or relating to the service is limited to
        the greater of (a) the amount you paid us in the twelve months before
        the claim arose, or (b) one hundred US dollars. We're not liable for
        indirect, incidental, consequential, or punitive damages.
      </p>

      <h2>11. Changes to these Terms</h2>
      <p>
        We may update these Terms from time to time. If we make a material
        change, we'll let you know in-app or by email. Continued use after a
        change means you accept the updated Terms.
      </p>

      <h2>12. Governing law</h2>
      <p>
        These Terms are governed by the laws of the jurisdiction in which
        PaperBrain operates, without regard to conflict-of-law rules. Any
        dispute will be resolved in the courts of that jurisdiction unless
        applicable law requires otherwise.
      </p>

      <h2>13. Contact</h2>
      <div className="legal-contact">
        Questions about these Terms? Email{' '}
        <a href="mailto:hello@bytesizedbits.com">hello@bytesizedbits.com</a>.
      </div>
    </LegalLayout>
  );
};
